import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { bulkCreateDeepDive } from '../../store/actions/BatchTables/BatchTablesActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';

import Button from '../shared/TailwindComponents/Button';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import Badge from '../shared/TailwindComponents/Badge';
import { TableDetail, TableRow } from '../../types/batchTables';
import {
  PiCheckCircleLight,
  PiXCircleLight,
  PiWarningCircleLight,
  PiMinusCircleLight,
  PiFileTextLight,
  PiArrowSquareOutLight,
} from 'react-icons/pi';

interface BulkReviewPanelProps {
  table: TableDetail;
  onClose: () => void;
  /** When opened from the multi-select toolbar, restrict the default selection to these row ids. */
  preselectedRowIds?: number[];
}

interface RowSummary {
  row: TableRow;
  fileName: string;
  hasExistingReview: boolean;
  existingReviewUuid: string | null;
  counts: {
    compliant: number;
    non_compliant: number;
    needs_review: number;
    not_found: number;
  };
  needsReview: boolean; // any cell in non_compliant/needs_review/not_found
}

const summarizeRow = (row: TableRow): RowSummary => {
  const counts = { compliant: 0, non_compliant: 0, needs_review: 0, not_found: 0 };
  Object.values(row.cells || {}).forEach((cell: any) => {
    const s = cell?.compliance_status;
    if (s && s in counts) counts[s as keyof typeof counts] += 1;
  });
  const needsReview = counts.non_compliant + counts.needs_review + counts.not_found > 0;
  return {
    row,
    fileName: (row as any).file_name || `Document ${row.id}`,
    hasExistingReview: !!(row as any).review_session_id || !!(row as any).review_session_uuid,
    existingReviewUuid: (row as any).review_session_uuid || null,
    counts,
    needsReview,
  };
};

const BulkReviewPanel: React.FC<BulkReviewPanelProps> = ({ table, onClose, preselectedRowIds }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const summaries = useMemo<RowSummary[]>(
    () => (table.rows || []).map(summarizeRow),
    [table.rows]
  );

  const [selected, setSelected] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    if (preselectedRowIds && preselectedRowIds.length > 0) {
      // Opened from multi-select toolbar - honor the user's row picks (excluding any with existing reviews)
      const reviewableIds = new Set(summaries.filter((s) => !s.hasExistingReview).map((s) => s.row.id));
      preselectedRowIds.forEach((id) => {
        if (reviewableIds.has(id)) initial.add(id);
      });
      return initial;
    }
    // Opened from header button - default to rows that need review and don't already have one
    summaries.forEach((s) => {
      if (s.needsReview && !s.hasExistingReview) initial.add(s.row.id);
    });
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);

  const toggle = (rowId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const selectableRowIds = useMemo(
    () => summaries.filter((s) => !s.hasExistingReview).map((s) => s.row.id),
    [summaries]
  );

  const allSelectableSelected = selectableRowIds.length > 0 && selectableRowIds.every((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelectableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableRowIds));
    }
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    const result = await dispatch(bulkCreateDeepDive(table.uuid, Array.from(selected)));
    setSubmitting(false);

    if (result?.review_uuid) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'success',
        message: t('Repaper review started'),
        subMsg: t('Reviewing {{count}} documents', { count: result.document_count }),
        duration: 4000,
      }));
      // `onClose`, not `hideSidePanel`: this renders in a surface pane on the
      // table page and in the global side panel elsewhere, and only the host
      // knows how to dismiss itself.
      onClose();
      history.push(`/reviews/${result.review_uuid}`);
    }
  };

  const selectedNeedsReviewCount = useMemo(
    () => summaries.filter((s) => selected.has(s.row.id) && s.needsReview).length,
    [summaries, selected]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar-main">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('Run a single review session over the selected documents. Documents with non-compliant, needs-review, or not-found findings are pre-selected.')}
        </p>

        {/* Select-all */}
        <div className="flex items-center justify-between mb-3 px-3 py-2 bg-surface-sunken/70 border border-hairline-soft rounded-lg">
          <CheckBox
            label={t('Select all reviewable')}
            translate={false}
            checked={allSelectableSelected}
            onChange={toggleAll}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('{{n}} of {{total}} selected', { n: selected.size, total: selectableRowIds.length })}
          </span>
        </div>

        {/* Row list */}
        <div className="space-y-2">
          {summaries.map((s) => {
            const isSelected = selected.has(s.row.id);
            const isDisabled = s.hasExistingReview;
            return (
              <div
                key={s.row.id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                  isDisabled
                    ? 'bg-surface-sunken/60 border-hairline-soft opacity-70'
                    : isSelected
                    ? 'bg-primary-50/50 dark:bg-primary-900/15 border-primary-300 dark:border-primary-700'
                    : 'bg-surface border-hairline hover:border-hairline-soft cursor-pointer'
                }`}
                onClick={() => !isDisabled && toggle(s.row.id)}
              >
                <CheckBox
                  label={t('Select document')}
                  hideLabel
                  translate={false}
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={(e) => { e.stopPropagation(); toggle(s.row.id); }}
                  className="mt-0.5"
                />

                <PiFileTextLight className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{s.fileName}</span>
                    {isDisabled && (
                      <Badge color="gray">{t('Already reviewed')}</Badge>
                    )}
                  </div>

                  {/* Status counts */}
                  <div className="flex items-center gap-3 mt-1.5 text-xs">
                    {s.counts.non_compliant > 0 && (
                      <span className="flex items-center gap-1 text-danger-600 dark:text-danger-400">
                        <PiXCircleLight className="w-3.5 h-3.5" />
                        {s.counts.non_compliant} {t('non-compliant')}
                      </span>
                    )}
                    {s.counts.needs_review > 0 && (
                      <span className="flex items-center gap-1 text-warning-600 dark:text-warning-400">
                        <PiWarningCircleLight className="w-3.5 h-3.5" />
                        {s.counts.needs_review} {t('needs review')}
                      </span>
                    )}
                    {s.counts.not_found > 0 && (
                      <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <PiMinusCircleLight className="w-3.5 h-3.5" />
                        {s.counts.not_found} {t('not found')}
                      </span>
                    )}
                    {s.counts.compliant > 0 && (
                      <span className="flex items-center gap-1 text-success-600 dark:text-success-400">
                        <PiCheckCircleLight className="w-3.5 h-3.5" />
                        {s.counts.compliant} {t('compliant')}
                      </span>
                    )}
                  </div>

                  {isDisabled && s.existingReviewUuid && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(`/reviews/${s.existingReviewUuid}`);
                        onClose();
                      }}
                      className="mt-1 inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {t('Open existing review')}
                      <PiArrowSquareOutLight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-hairline px-5 py-4 flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {selected.size > 0 && selectedNeedsReviewCount < selected.size && (
            t('{{n}} compliant docs included', { n: selected.size - selectedNeedsReviewCount })
          )}
        </span>
        <div className="flex gap-2">
          <Button variant="neutral" size="small" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleSubmit}
            disabled={selected.size === 0 || submitting}
          >
            {submitting
              ? t('Starting...')
              : t('Start review for {{n}} documents', { n: selected.size })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkReviewPanel;
